import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const initialOrderForm = {
  productCode: '',
  quantity: '',
  person: '',
  address: '',
  status: 'Pending',
  weight: '',
  size: '',
  factoryName: '',
};

const OrderManagement = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [orderForm, setOrderForm] = useState(initialOrderForm);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]); // parentOrderId selections for export / bulk
  const [searchTerm, setSearchTerm] = useState('');
  const [subTypeFilter, setSubTypeFilter] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const printRef = useRef();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
  });

  // New: expanded groups & selected child ids per group
  const [expandedGroups, setExpandedGroups] = useState([]); // array of parentOrderId
  const [selectedChildIds, setSelectedChildIds] = useState({}); // { [parentOrderId]: [childOrderId, ...] }
  const [showDeliveredOnly, setShowDeliveredOnly] = useState(false);

  // Sorting + Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (!user) return;
    const fetchUsers = async () => {
      if (user?.role === 'admin') {
        try {
          const data = await api.getUsers();
          setAllUsers(data);
        } catch (err) {
          console.error('Failed to fetch users', err);
        }
      }
    };
    fetchUsers();
  }, [user]);

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await api.getOrders();
      setOrders(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await api.getAllProducts();
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrderForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key === column) {
        return { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: column, direction: 'asc' };
    });
  };

  const getProductDetails = (code) =>
    (products || []).find((p) => p.code === code) || {};

  const filteredOrders = orders.filter((order) => {
    const product = getProductDetails(order.productCode);

    const username = user?.username?.toLowerCase();

    const userSubType = user?.subType?.trim().toLowerCase() || null;

    if (username === "admin") {
      // Admin → can see all
    } else if (userSubType) {
      // User with subType → must match either order.subType or product.subType
      const orderSubType = order.subType?.trim().toLowerCase() || "";
      const productSubType = product?.subType?.trim().toLowerCase() || "";

      if (orderSubType !== userSubType && productSubType !== userSubType) {
        return false;
      }
    } else {
      // No subType → only their own orders
      if (order.person?.toLowerCase() !== username) return false;
    }
    const normalizedStatus = (order.status || "").trim().toLowerCase();

    if (showDeliveredOnly) {
      // Show only delivered
      if (normalizedStatus !== "delivered") return false;
    } else {
      // Hide delivered by default
      if (normalizedStatus === "delivered") return false;
    }

    // 🔍 Search filter
    const allFields = [
      order.parentOrderId,
      order.productName,
      order.productCode,
      order.weight,
      order.size,
      order.quantity,
      order.person,
      order.status,
      order.address,
      order.subType,
      product.subType,
      product.name,
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch = allFields.includes(searchTerm);

    // 🏭 SubType dropdown filter
    const matchesSubType = subTypeFilter
      ? order.subType === subTypeFilter || product.subType === subTypeFilter
      : true;

    return matchesSearch && matchesSubType;
  });

  const isWithin7Days = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const target = new Date(dateStr);
    const diffTime = target - today;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 7;
  };

  // Sorting
  const sortedOrders = [...filteredOrders]
    .sort((a, b) => {
      // First: expected date within 7 days
      const aUrgent = isWithin7Days(a.address) ? 0 : 1;
      const bUrgent = isWithin7Days(b.address) ? 0 : 1;
      if (aUrgent !== bUrgent) return aUrgent - bUrgent;

      // Then: existing sort logic
      let valA, valB;
      switch (sortConfig.key) {
        case 'date':
          valA = new Date(a.date);
          valB = new Date(b.date);
          break;
        case 'quantity':
          valA = Number(a.quantity);
          valB = Number(b.quantity);
          break;
        default:
          valA = (a[sortConfig.key] || '').toString().toLowerCase();
          valB = (b[sortConfig.key] || '').toString().toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });


  // === Group orders by parentOrderId ===
  const groupedOrders = Object.values(
    sortedOrders.reduce((acc, order) => {
      const id = order.parentOrderId || `__noParent__${order.id}`; // fallback
      if (!acc[id]) {
        acc[id] = {
          parentOrderId: id,
          orders: [order],
          // keep representative fields on the group (first order's values)
          date: order.date,
          person: order.person,
          subType: order.subType,
          address: order.address,
          remark: order.remark,
          status: order.status,
        };
      } else {
        acc[id].orders.push(order);
      }
      return acc;
    }, {})
  );

  const calculateTotals = (orders) => {
    let totalWeight = 0;
    let totalPieces = 0;

    orders.forEach((o) => {
      const w = o.weight?.toString() || "";
      const qty = parseInt(o.quantity) || 1;
      const product = getProductDetails(o.productCode);
      const unitWeight = parseFloat(product?.weight) || 0;

      if (w.toLowerCase().includes("pieces")) {
        const pieces = parseInt(w) || 0;
        totalPieces += pieces * qty;
        totalWeight += pieces * unitWeight * qty; // convert pieces → grams
      } else {
        totalWeight += (parseFloat(w) || 0) * qty;
      }
    });

    return { totalWeight, totalPieces };
  };


  // Flatten all orders for total calculation
  const exportDataFlat = groupedOrders.flatMap(g => g.orders);

  // Calculate totals
  const { totalWeight, totalPieces } = calculateTotals(exportDataFlat);


  // Pagination (use groupedOrders)
  const totalPages = Math.ceil(groupedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = groupedOrders.slice(startIndex, startIndex + itemsPerPage);

  // const totalWeight = sortedOrders.reduce((sum, o) => {
  //   const w = parseFloat(o.weight) || 0;
  //   const qty = parseInt(o.quantity) || 1;
  //   return sum + w * qty;
  // }, 0);

  // Export Helpers
  const getExportData = () => {
    if (selectedOrders.length > 0) {
      return groupedOrders.filter((g) => selectedOrders.includes(g.parentOrderId));
    }
    return groupedOrders;
  };

  const formatWeight = (order) => {
    const w = order.weight?.toString() || "";
    const qty = parseInt(order.quantity) || 1;
    const product = getProductDetails(order.productCode);
    const unitWeight = parseFloat(product?.weight) || 0;

    if (w.toLowerCase().includes("pieces")) {
      const pieces = parseInt(w) || 0;
      const totalPieces = pieces * qty;
      const totalWeight = pieces * unitWeight * qty;
      return `${totalPieces} pcs / ${totalWeight} g`;
    } else {
      const totalWeight = (parseFloat(w) || 0) * qty;
      return `${totalWeight} g`;
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    const exportData = getExportData();

    const data = exportData.flatMap((g) =>
      g.orders.map((o) => ({
        OrderID: g.parentOrderId,
        CreatedAt: new Date(o.date).toLocaleDateString('en-CA'),
        ProductName: o.productName,
        Code: o.productCode,
        "pieces/weight": formatWeight(o),
        Size: o.size,
        Color: o.color,
        Person: o.person,
        Factory: o.subType,
        ExpectedDate: o.address,
        Status: o.status,
      }))
    );

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, 'Orders.xlsx');
  };

  // Export PDF
  const handleExportPDF = () => {
    const exportData = getExportData(); // your filtered/export data

    if (!exportData || exportData.length === 0) {
      alert("No orders to export");
      return;
    }

    // Group the orders by productCode
    const groupedOrders = exportData.reduce((acc, g) => {
      g.orders.forEach((o) => {
        if (!acc[o.productCode]) {
          acc[o.productCode] = [];
        }
        acc[o.productCode].push({ ...o, parentOrderId: g.parentOrderId });
      });
      return acc;
    }, {});

    const tableRows = Object.keys(groupedOrders)
      .sort((a, b) => {
        const productA = getProductDetails(a);
        const productB = getProductDetails(b);

        const nameA = (productA?.name || "").trim().toLowerCase();
        const nameB = (productB?.name || "").trim().toLowerCase();

        // 🔹 First sort by product name
        const nameCompare = nameA.localeCompare(nameB);
        if (nameCompare !== 0) return nameCompare;

        // 🔹 Then sort by product code (numeric-aware)
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      })
      .flatMap((productCode) => {
        const productOrders = groupedOrders[productCode];
        const product = getProductDetails(productCode);

        const imageUrl = product?.image
          ? `https://azadi.ae/backend${product.image}`
          : "";
        const productName = product?.name || "";

        // ✅ SORT BY COLOR → THEN BY NUMERIC SIZE
        productOrders.sort((a, b) => {
          const colorA = a.color?.trim() || "";
          const colorB = b.color?.trim() || "";
          const colorCompare = colorA.localeCompare(colorB);

          if (colorCompare !== 0) return colorCompare;

          // Sort size numerically (VARCHAR → number)
          const sizeA = parseFloat(a.size?.toString().trim()) || 0;
          const sizeB = parseFloat(b.size?.toString().trim()) || 0;

          return sizeA - sizeB;
        });

        // Rowspanned header cells
        const headerCells = `
        <td rowspan="${productOrders.length}" style="text-align:center;">
          ${imageUrl
            ? `<img src="${imageUrl}" style="max-width:130px;max-height:80px;object-fit:contain;border-radius:6px;" />`
            : `<div style="width:60px;height:60px;background:#f9f9f9;border-radius:6px;color:#999;font-size:10px;display:flex;align-items:center;justify-content:center;">No Img</div>`
          }
        </td>
        <td rowspan="${productOrders.length}" style="text-align:center;font-weight:bold;">${productName}</td>
        <td rowspan="${productOrders.length}" style="text-align:center;font-weight:bold;">${productCode}</td>
      `;

        // Generate each row
        const rows = productOrders.map((o, index) => {
          return index === 0
            ? `
            <tr>
              ${headerCells}
              <td>${o.parentOrderId}</td>
              <td>${new Date(o.date).toLocaleDateString("en-CA")}</td>
              <td>${o.person || "-"}</td>
              <td>${o.subType || "-"}</td>
              <td>${o.color || "-"}</td>
              <td>${o.size}</td>
              <td>${formatWeight(o)}</td>
              <td>${o.address || "N/A"}</td>
              <td>${o.remark || "-"}</td>
              <td>${o.status}</td>
            </tr>`
            : `
            <tr>
              <td>${o.parentOrderId}</td>
              <td>${new Date(o.date).toLocaleDateString("en-CA")}</td>
              <td>${o.person || "-"}</td>
              <td>${o.subType || "-"}</td>
              <td>${o.color || "-"}</td>
              <td>${o.size}</td>
              <td>${formatWeight(o)}</td>        
              <td>${o.address || "N/A"}</td>
              <td>${o.remark || "-"}</td>
              <td>${o.status}</td>
            </tr>`;
        });

        return rows;
      })
      .join("");

    // Open print window
    const printWindow = window.open("", "", "width=1200,height=800");

    printWindow.document.write(`
    <html>
      <head>
        <title>Orders Report</title>
        <style>
          @media print {
            @page {
              size: landscape;
            }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            color: #000;
            background: #fff;
          }
          h2 {
            text-align: center;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #444;
            padding: 6px 8px;
            text-align: center;
            vertical-align: middle;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          td img {
            max-width: 130px;
            max-height: 80px;
            object-fit: contain;
            border-radius: 6px;
            background-color: #fff8e1;
            padding: 4px;
          }
        </style>
      </head>
      <body>
        <h2>Orders Report</h2>
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product Name</th>
              <th>Product Code</th>
              <th>OrderID</th>
              <th>Created At</th>
              <th>Person</th>
              <th>Factory</th>
              <th>Color</th>
              <th>Size</th>
              <th>pieces/weight</th>
              <th>Expected Date</th>
              <th>Remark</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };




  // Create/Edit Order
  const handleCreateOrUpdate = async () => {
    const { parentOrderId, productCode, quantity, person, address, status, weight, size, factoryName, remark } = orderForm;
    const missingFields = [];

    if (!productCode) missingFields.push('Product Code');
    if (!quantity) missingFields.push('Quantity');
    if (!person) missingFields.push('Person Name');
    if (!status) missingFields.push('Status');
    if (!factoryName) missingFields.push('Factory Name');

    if (missingFields.length > 0) {
      alert(`Please fill the following field(s):\n- ${missingFields.join('\n- ')}`);
      return;
    }

    const product = getProductDetails(productCode);

    const orderData = {
      parentOrderId,
      productCode,
      quantity: parseInt(quantity),
      person,
      address,
      status,
      weight: weight || '',
      size: size || '',
      productName: product.name || '',
      subType: factoryName,    // Save factoryName here as subType
      remark: remark,
      date: new Date().toISOString().split('T')[0],
    };

    try {
      const parentOrderId = `KAJ${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(100 + Math.random() * 900)}`;
      if (editingOrder) {
        await api.updateOrder(editingOrder.id, orderData);
      } else {
        const updatedOrderData = { ...orderData, parentOrderId }; // Adding parentOrderId
        await api.createOrder(updatedOrderData);
      }
      setShowModal(false);
      setOrderForm(initialOrderForm);
      setEditingOrder(null);
      loadOrders();
    } catch (err) {
      alert(err.message);
    }

  };

  const handleEdit = (orderOrGroup) => {
    // If group passed, open edit for first child (preserve original behavior)
    const order = orderOrGroup.orders ? orderOrGroup.orders[0] : orderOrGroup;

    setEditingOrder(order);
    setOrderForm({
      parentOrderId: order.parentOrderId,
      productCode: order.productCode,
      quantity: order.quantity,
      person: order.person,
      address: order.address,
      status: order.status || 'Pending',
      weight: order.weight || '',
      size: order.size || '',
      remark: order.remark || '',
      factoryName: order.subType || '',  // prefill factoryName from subType
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      message: "⚠ Are you sure you want to delete this order?",
      onConfirm: async () => {
        await api.deleteOrder(id);
        loadOrders();
        setConfirmDialog({ open: false, message: '', onConfirm: null });
      },
    });
  };

  // New: toggle expand group
  const toggleExpandGroup = (parentOrderId) => {
    setExpandedGroups((prev) =>
      prev.includes(parentOrderId)
        ? prev.filter((p) => p !== parentOrderId)
        : [...prev, parentOrderId]
    );
  };

  // New: toggle child checkbox
  const toggleChildSelection = (parentOrderId, childId) => {
    setSelectedChildIds((prev) => {
      const curr = prev[parentOrderId] ? [...prev[parentOrderId]] : [];
      if (curr.includes(childId)) {
        return { ...prev, [parentOrderId]: curr.filter((c) => c !== childId) };
      } else {
        return { ...prev, [parentOrderId]: [...curr, childId] };
      }
    });
  };

  // New: delete selected children in a group
  const deleteSelectedChildren = (parentOrderId) => {
    const childIds = selectedChildIds[parentOrderId] || [];
    if (childIds.length === 0) {
      alert('Please select at least one item to delete.');
      return;
    }
    setConfirmDialog({
      open: true,
      message: `⚠ Are you sure you want to delete ${childIds.length} item(s) from ${parentOrderId}?`,
      onConfirm: async () => {
        try {
          for (const id of childIds) {
            await api.deleteOrder(id);
          }
          // clear selection for that group
          setSelectedChildIds((prev) => {
            const copy = { ...prev };
            delete copy[parentOrderId];
            return copy;
          });
          setConfirmDialog({ open: false, message: '', onConfirm: null });
          loadOrders();
        } catch (err) {
          alert('Failed to delete some items: ' + err.message);
        }
      },
    });
  };

  const taupeColor = "#A89F91";
  const darkText = "#3C3B3F";
  const bgColor = "#F4F1EE";
  const buttonBg = "#BFA96B";
  const buttonHover = "#A1893E";

  return (
    <div className="p-6 bg-[#F4F1EE] text-[#3C3B3F] min-h-screen">
      {/* Header Actions */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <h2 className="text-2xl font-bold text-[#3C3B3F]">Order Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowModal(true)}
            className="px-3 py-1 bg-[#BFA96B] hover:bg-[#A1893E] text-white rounded"
          >
            + Create Order
          </button>
          <button
            onClick={handleExportExcel}
            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded"
          >
            Export Excel
          </button>
          <button
            onClick={handleExportPDF}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded"
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          className="border border-[#A89F91] text-[#3C3B3F] px-3 py-1 rounded w-64 bg-white"
        />
        <select
          value={subTypeFilter}
          onChange={(e) => setSubTypeFilter(e.target.value)}
          className="border border-[#A89F91] text-[#3C3B3F] px-3 py-1 rounded bg-white"
        >
          <option value="">All Factories</option>
          {Array.from(new Set(products.map((p) => p.subType).filter(Boolean))).map((st) => (
            <option key={st} value={st}>{st}</option>
          ))}
        </select>
      </div>

      {/* Counts + Pagination */}
      <div className="flex justify-between items-center mb-3">
        <button
          onClick={() => setShowDeliveredOnly((prev) => !prev)}
          className={`px-3 py-1 rounded text-white ${showDeliveredOnly ? 'bg-green-700 hover:bg-green-800' : 'bg-gray-500 hover:bg-gray-600'
            }`}
        >
          {showDeliveredOnly ? 'Showing Delivered Orders' : 'Show Delivered Orders'}
        </button>
        <span className="text-sm text-gray-700">
          Showing {paginatedOrders.length} of {groupedOrders.length} grouped orders (Total: {filteredOrders.length}) | Total Weight: {totalWeight} | Total Pieces: {totalPieces}
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1"
            >
              {[10, 20, 30, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>entries</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-1 px-2 border-b">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrders(paginatedOrders.map((o) => o.parentOrderId));
                    } else {
                      setSelectedOrders([]);
                    }
                  }}
                  checked={
                    selectedOrders.length > 0 &&
                    paginatedOrders.every((o) => selectedOrders.includes(o.parentOrderId))
                  }
                />
              </th>
              {[
                { label: 'Order ID', key: 'parentOrderId' },
                { label: 'Created At', key: 'date' },
                // { label: 'Product Name', key: 'productName' },
                { label: 'Code', key: 'productCode' },
                { label: 'pieces/weight', key: 'weight' },
                { label: 'Size', key: 'size' },
                { label: 'Color', key: 'color' },
                // { label: 'Person', key: 'person' },
                { label: 'Factory Name', key: 'subType' },
                { label: 'Expected Date', key: 'address' },
                { label: 'Remark', key: 'remark' },
                { label: 'Status', key: 'status' },
              ].map(({ label, key }) => (
                <th
                  key={key}
                  className="py-1 px-2 border-b cursor-pointer select-none max-w-[140px] truncate"
                  onClick={() => handleSort(key)}
                  title={label}
                >
                  {label}{' '}
                  {sortConfig.key === key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
              ))}
              <th className="py-1 px-2 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="15" className="text-center py-4 text-gray-500">
                  No matching orders found
                </td>
              </tr>
            ) : (
              paginatedOrders.map((group) => {
                // combine fields from child orders
                const productNames = group.orders.map((o) => o.productName || getProductDetails(o.productCode).name).join(", ");
                const productCodes = group.orders.map((o) => o.productCode).join(", ");
                const sizes = group.orders.map((o) => o.size || "-").join(", ");
                const weights = group.orders.map((o) => o.weight || "-").join(", ");
                const colors = group.orders.map((o) => o.color || "-").join(", ");

                // find product image for first child that has it
                const firstProductWithImage = group.orders
                  .map((o) => getProductDetails(o.productCode))
                  .find((p) => p && p.image);

                const isExpanded = expandedGroups.includes(group.parentOrderId);
                const groupChildSelected = selectedChildIds[group.parentOrderId] || [];

                return (
                  <React.Fragment key={group.parentOrderId}>
                    <tr className={`text-center border-b`}>
                      <td className="py-1 px-2">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(group.parentOrderId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrders((prev) => [...prev, group.parentOrderId]);
                            } else {
                              setSelectedOrders((prev) => prev.filter((id) => id !== group.parentOrderId));
                            }
                          }}
                        />
                      </td>
                      <td className="py-1 px-2 max-w-[130px] truncate" title={group.parentOrderId}>
                        <div className="flex flex-col items-center">
                          <div>{group.parentOrderId}</div>
                          {firstProductWithImage && firstProductWithImage.image && (
                            <div className="w-24 h-24 mt-1 overflow-hidden rounded-lg shadow flex items-center justify-center bg-[#fff8e1]">
                              <img
                                src={`https://azadi.ae/backend${firstProductWithImage.image}`}
                                alt={firstProductWithImage.name}
                                className="max-w-full max-h-24 object-contain rounded-lg"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={new Date(group.date).toLocaleDateString('en-CA')}>
                        {new Date(group.date).toLocaleDateString('en-CA')}
                      </td>
                      {/* <td className="py-1 px-2 max-w-[100px] truncate" title={productNames}>
                        {productNames}
                      </td> */}
                      <td className="py-1 px-2 max-w-[100px] truncate" title={productCodes}>{productCodes}</td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={weights}>{weights}</td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={sizes}>{sizes}</td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={colors}>{colors}</td>
                      {/* <td className="py-1 px-2 max-w-[100px] truncate" title={group.person}>{group.person}</td>  */}
                      <td className="py-1 px-2 max-w-[100px] truncate" title={group.subType}>{group.subType || '-'}</td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={group.address}
                        style={{
                          backgroundColor: isWithin7Days(group.address) ? '#FFF3CD' : 'transparent', // light yellow
                          fontWeight: isWithin7Days(group.address) ? 'bold' : 'normal',
                        }}
                      >{group.address}
                      </td>
                      <td className="py-1 px-2 max-w-[150px] truncate" title={group.remark}>{group.remark}</td>
                      <td className="py-1 px-2 max-w-[100px] truncate" title={group.status}>{group.status}</td>
                      <td className="py-1 px-2 space-x-2">
                        <button onClick={() => handleEdit(group)} className="text-blue-600">Edit</button>
                        <button onClick={() => handleDelete(group.orders[0].id)} className="text-red-600">Delete</button>
                        <button
                          onClick={() => toggleExpandGroup(group.parentOrderId)}
                          className="text-gray-600 ml-2"
                          title={isExpanded ? 'Collapse items' : 'Show items'}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded area: child items with checkboxes */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="15" className="py-2 px-4">
                          <div className="space-y-4">
                            {/* Header + actions for child items */}
                            <div className="flex justify-between items-center">
                              <div className="text-sm font-medium">Items in {group.parentOrderId}</div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const allChildIds = group.orders.map((o) => o.id);
                                    if ((selectedChildIds[group.parentOrderId] || []).length === allChildIds.length) {
                                      setSelectedChildIds((prev) => ({ ...prev, [group.parentOrderId]: [] }));
                                    } else {
                                      setSelectedChildIds((prev) => ({ ...prev, [group.parentOrderId]: allChildIds }));
                                    }
                                  }}
                                  className="px-2 py-1 border rounded text-sm"
                                >
                                  {(selectedChildIds[group.parentOrderId] || []).length === group.orders.length ? 'Unselect All' : 'Select All'}
                                </button>

                                <button
                                  onClick={() => deleteSelectedChildren(group.parentOrderId)}
                                  className="px-2 py-1 bg-red-600 text-white rounded text-sm"
                                >
                                  Delete Selected
                                </button>
                                <button
                                  onClick={() => {
                                    const childIds = selectedChildIds[group.parentOrderId] || [];
                                    if (childIds.length === 0) {
                                      alert('Please select at least one item to set expected date.');
                                      return;
                                    }

                                    const newDate = prompt('Enter new expected delivery date (YYYY-MM-DD):');
                                    if (!newDate) return;

                                    setConfirmDialog({
                                      open: true,
                                      message: `Are you sure you want to set expected date to ${newDate} for ${childIds.length} item(s)?`,
                                      onConfirm: async () => {
                                        try {
                                          for (const id of childIds) {
                                            const order = group.orders.find(o => o.id === id);
                                            await api.updateOrder(id, { ...order, address: newDate });
                                          }
                                          setConfirmDialog({ open: false, message: '', onConfirm: null });
                                          loadOrders();
                                        } catch (err) {
                                          alert('Failed to update items: ' + err.message);
                                        }
                                      },
                                    });
                                  }}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-sm"
                                >
                                  Set Expected Date
                                </button>

                                <button
                                  onClick={() => {
                                    const childIds = selectedChildIds[group.parentOrderId] || [];
                                    if (childIds.length === 0) {
                                      alert("Please select at least one item to update status.");
                                      return;
                                    }

                                    const newStatus = prompt("Enter new status (e.g., Pending, Partial, Delivered, Cancelled):");
                                    if (!newStatus) return;

                                    setConfirmDialog({
                                      open: true,
                                      message: `Are you sure you want to update status to "${newStatus}" for ${childIds.length} item(s)?`,
                                      confirmText: "Update",
                                      onConfirm: async () => {
                                        try {
                                          for (const id of childIds) {
                                            const order = group.orders.find(o => o.id === id);
                                            await api.updateOrder(id, { ...order, status: newStatus });
                                          }
                                          setConfirmDialog({ open: false, message: "", onConfirm: null });
                                          loadOrders();
                                        } catch (err) {
                                          alert("Failed to update items: " + err.message);
                                        }
                                      },
                                    });
                                  }}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-sm"
                                >
                                  Set Status
                                </button>


                              </div>
                            </div>

                            {/* Group children by productCode */}
                            {Object.entries(
                              group.orders.reduce((acc, child) => {
                                const productCode = child.productCode;
                                if (!acc[productCode]) acc[productCode] = [];
                                acc[productCode].push(child);
                                return acc;
                              }, {})
                            ).map(([productCode, children]) => {
                              const product = getProductDetails(productCode);
                              const displayName = product?.name || productCode;

                              return (
                                <div key={productCode} className="space-y-2">
                                  {/* Product Code header */}
                                  <div className="font-semibold text-gray-700 text-sm">{displayName} ({productCode})</div>

                                  {/* Children of this product */}
                                  {children
                                    .sort((a, b) => {
                                      const colorA = (a.color || "").toLowerCase();
                                      const colorB = (b.color || "").toLowerCase();
                                      if (colorA !== colorB) return colorA.localeCompare(colorB);
                                      const sizeA = parseFloat(a.size) || 0;
                                      const sizeB = parseFloat(b.size) || 0;
                                      return sizeA - sizeB;
                                    })
                                    .map((child) => (
                                      <div key={child.id} className="flex items-center justify-between bg-white border rounded p-2"
                                        style={{
                                          backgroundColor: isWithin7Days(child.address) ? "#FFF9E6" : "white",
                                          borderColor: isWithin7Days(child.address) ? "#F4C56A" : "#e5e7eb",
                                        }}
                                      >
                                        <div className="flex items-center gap-3">
                                          <input
                                            type="checkbox"
                                            checked={(selectedChildIds[group.parentOrderId] || []).includes(child.id)}
                                            onChange={() => toggleChildSelection(group.parentOrderId, child.id)}
                                          />
                                          {product?.image ? (
                                            <div className="w-12 h-12 overflow-hidden rounded bg-[#fff8e1] flex items-center justify-center">
                                              <img src={`https://azadi.ae/backend${product.image}`} alt={product.name} className="max-w-full max-h-12 object-contain" />
                                            </div>
                                          ) : (
                                            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded text-xs text-gray-500">No Img</div>
                                          )}
                                          <div className="text-left text-sm">
                                            <div className="font-medium">{child.productName || product?.name || child.productCode}</div>
                                            <div className="text-xs text-gray-600">
                                              Code: {child.productCode} • Weight: {child.weight || '-'} • Size: {child.size} • Color: {child.color} • Expected Date: <span
                                                style={{
                                                  backgroundColor: isWithin7Days(child.address) ? "#FFE8A6" : "transparent",
                                                  fontWeight: isWithin7Days(child.address) ? "bold" : "normal",
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                }}
                                              >{child.address}</span> • Status: {child.status} • Remark: {child.remark}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                          <button onClick={() => handleEdit(child)} className="text-blue-600 text-sm">Edit</button>
                                          <button
                                            onClick={() => {
                                              setConfirmDialog({
                                                open: true,
                                                message: `⚠ Are you sure you want to delete item ${child.productCode}?`,
                                                onConfirm: async () => {
                                                  await api.deleteOrder(child.id);
                                                  loadOrders();
                                                  setConfirmDialog({ open: false, message: '', onConfirm: null });
                                                },
                                              });
                                            }}
                                            className="text-red-600 text-sm"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}


                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-4">
          <div
            className="rounded p-6 w-full max-w-md"
            style={{ backgroundColor: bgColor, color: darkText }}
          >
            <h3 className="text-lg font-semibold mb-4">
              {editingOrder ? "Edit Order" : "Create Order"}
            </h3>

            {/* Product Code */}
            <label className="block mb-2">
              Order Id: <span className="text-red-600">*</span>
              <input
                name="parentOrderId"
                value={orderForm.parentOrderId}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter order ID"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            <label className="block mb-2">
              Product Code: <span className="text-red-600">*</span>
              <input
                name="productCode"
                value={orderForm.productCode}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter product code"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Product Details Preview */}
            {orderForm.productCode && (
              <div
                className="mb-2 text-sm rounded p-2"
                style={{ backgroundColor: "#f8f8f8", color: darkText }}
              >
                <p>
                  <strong>Product:</strong>{" "}
                  {getProductDetails(orderForm.productCode)?.name || "Unknown Product"}
                </p>
              </div>
            )}

            {/* Quantity */}
            <label className="block mb-2">
              Quantity: <span className="text-red-600">*</span>
              <input
                name="quantity"
                type="number"
                min="1"
                value={orderForm.quantity}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Weight */}
            <label className="block mb-2">
              Weight:
              <input
                name="weight"
                value={orderForm.weight}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter weight"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Size */}
            <label className="block mb-2">
              Size:
              <input
                name="size"
                value={orderForm.size}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter size"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Person Name */}
            {user?.role === "admin" ? (
              <label className="block mb-2">
                Person Name: <span className="text-red-600">*</span>
                <select
                  name="person"
                  value={orderForm.person}
                  onChange={handleChange}
                  className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                  style={{
                    borderColor: taupeColor,
                    backgroundColor: "#fff",
                    color: darkText,
                  }}
                >
                  <option value="">Select user</option>
                  {allUsers.map((u) => (
                    <option key={u.username} value={u.username}>
                      {u.username}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                <input type="hidden" name="person" value={orderForm.person} readOnly />
                <label className="block mb-2">
                  Person Name:
                  <input
                    value={orderForm.person}
                    readOnly
                    disabled
                    className="w-full border rounded mt-1 px-2 py-1 cursor-not-allowed"
                    style={{
                      backgroundColor: "#f3f3f3",
                      borderColor: taupeColor,
                      color: darkText,
                    }}
                  />
                </label>
              </>
            )}

            {/* Expected Delivery Date */}
            <label className="block mb-2">
              Expected Delivery Date: <span className="text-red-600">*</span>
              <input
                type="date"
                name="address"
                value={orderForm.expectedDeliveryDate}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Factory Name */}
            <label className="block mb-2">
              Factory Name: <span className="text-red-600">*</span>
              <input
                name="factoryName"
                value={orderForm.factoryName}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter factory name"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            <label className="block mb-2">
              Remark:
              <input
                name="remark"
                value={orderForm.remark}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                placeholder="Enter remark"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              />
            </label>

            {/* Status */}
            <label className="block mb-4">
              Status: <span className="text-red-600">*</span>
              <select
                name="status"
                value={orderForm.status}
                onChange={handleChange}
                className="w-full border rounded mt-1 px-2 py-1 focus:outline-none"
                style={{
                  borderColor: taupeColor,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
              >
                <option>Pending</option>
                <option>Partial</option>
                <option>Delivered</option>
                <option>Cancelled</option>
              </select>
            </label>

            {/* Buttons */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded font-bold transition-colors"
                style={{
                  border: `1px solid ${taupeColor}`,
                  backgroundColor: "#fff",
                  color: darkText,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                className="px-4 py-2 rounded font-bold transition-colors"
                style={{
                  backgroundColor: buttonBg,
                  color: darkText,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = buttonHover;
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = buttonBg;
                  e.currentTarget.style.color = darkText;
                }}
              >
                {editingOrder ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div
            className="bg-[#F4F1EE] text-[#3C3B3F] border border-[#A89F91] rounded-lg p-5 w-full max-w-sm shadow-lg"
          >
            <p className="mb-6 whitespace-pre-line">{confirmDialog.message}</p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-1 rounded border text-sm"
                style={{
                  backgroundColor: "#3C3B3F",
                  color: "#F4F1EE",
                  borderColor: "#A89F91",
                }}
                onClick={() =>
                  setConfirmDialog({ open: false, message: '', onConfirm: null })
                }
              >
                Cancel
              </button>
              <button
                className="px-4 py-1 rounded text-sm"
                style={{
                  backgroundColor: "#B22222",
                  color: "#fff",
                }}
                onClick={() => {
                  confirmDialog.onConfirm?.();
                }}
              >
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
